import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { getAdminSession } from '../../utils/adminSession';
import { getAdminAuthHeaders } from '../../utils/adminAuth';
import { addPlayerUrl, CACHE_KEYS, entriesUrl } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 700px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const FileInput = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const UploadError = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const ModePicker = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const ChoiceLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--gray-50);
`;

const ArchivedList = styled.div`
  display: grid;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #333;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.04);
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

type ArchivedPlayer = {
  name: string;
  position?: string;
  number?: number | string;
  shoots?: string;
  sponsor?: string;
  sponsorLink?: string;
  sponsorImg?: string;
  SponsorImg?: string;
  funfact?: string;
  imageUrl?: string;
  imageS3Key?: string;
  [key: string]: unknown;
};

const toTitleCase = (value: string): string => {
  if (!value) return value;

  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const normalizePosition = (value: string): string => {
  if (!value) return value;
  return value
    .trim()
    .split('/')
    .map((part) => {
      const p = part.trim();
      if (!p) return p;
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join('/');
};

const normalizeArchivedPlayer = (player: ArchivedPlayer) => ({
  name: String(player.name || '').trim(),
  position: typeof player.position === 'string' ? normalizePosition(player.position) : 'Forward',
  number: Number(player.number ?? 0),
  shoots: typeof player.shoots === 'string' && player.shoots.trim() ? player.shoots.trim() : undefined,
  sponsor: typeof player.sponsor === 'string' && player.sponsor.trim() ? player.sponsor.trim() : undefined,
  sponsorLink:
    typeof player.sponsorLink === 'string' && player.sponsorLink.trim() ? player.sponsorLink.trim() : undefined,
  sponsorImg:
    typeof player.sponsorImg === 'string' && player.sponsorImg.trim()
      ? player.sponsorImg.trim()
      : typeof player.SponsorImg === 'string' && player.SponsorImg.trim()
        ? player.SponsorImg.trim()
        : undefined,
  funfact: typeof player.funfact === 'string' && player.funfact.trim() ? player.funfact.trim() : undefined,
});

const AddPlayer: React.FC = () => {
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [number, setNumber] = useState('');
  const [shoots, setShoots] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorUrl, setSponsorUrl] = useState('');
  const [sponsorImageUrl, setSponsorImageUrl] = useState('');
  const [funFact, setFunFact] = useState('');
  const [playerImageBase64, setPlayerImageBase64] = useState<string | null>(null);
  const [playerImageFileName, setPlayerImageFileName] = useState<string | null>(null);
  const [isReadingPlayerImage, setIsReadingPlayerImage] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);
  const [playerMode, setPlayerMode] = useState<'fresh' | 'resign'>('fresh');
  const [archivedPlayers, setArchivedPlayers] = useState<ArchivedPlayer[]>([]);
  const [selectedArchivedPlayers, setSelectedArchivedPlayers] = useState<string[]>([]);
  const [archivedLoadError, setArchivedLoadError] = useState<string | null>(null);
  const [isLoadingArchivedPlayers, setIsLoadingArchivedPlayers] = useState(true);

  const session = getAdminSession();

  useEffect(() => {
    const loadArchivedPlayers = async () => {
      try {
        setArchivedLoadError(null);
        const players = await fetchItemsWithCache<ArchivedPlayer>(
          entriesUrl('tigers_players_archive'),
          CACHE_KEYS.playersArchive,
          5 * 60 * 1000
        );
        const sorted = [...(players ?? [])].filter((player) => !!player?.name).sort((a, b) => a.name.localeCompare(b.name));
        setArchivedPlayers(sorted);
      } catch (loadError) {
        setArchivedPlayers([]);
        const message = loadError instanceof Error ? loadError.message : 'Unable to load archived players.';
        setArchivedLoadError(message);
      } finally {
        setIsLoadingArchivedPlayers(false);
      }
    };

    loadArchivedPlayers();
  }, []);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handlePlayerImageFile = (file: File | null) => {
    setErrorMessage(null);

    if (!file) {
      setPlayerImageBase64(null);
      setPlayerImageFileName(null);
      setIsReadingPlayerImage(false);
      return;
    }

    const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPng) {
      setErrorMessage('Player image must be a PNG file.');
      return;
    }

    const maxBytes = 400 * 1024;
    if (file.size > maxBytes) {
      setErrorMessage('Player image must be 400KB or smaller.');
      return;
    }

    const reader = new FileReader();
    setIsReadingPlayerImage(true);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setPlayerImageBase64(base64);
      setPlayerImageFileName(file.name);
      setIsReadingPlayerImage(false);
    };
    reader.onerror = () => {
      setErrorMessage('Unable to read selected image file.');
      setIsReadingPlayerImage(false);
    };
    reader.readAsDataURL(file);
  };

  const buildFreshPlayerPayload = () => ({
    teamName: 'tigers',
    name: toTitleCase(name),
    position: normalizePosition(position),
    number: Number(number),
    shoots: shoots || undefined,
    sponsor: sponsorName || undefined,
    sponsorLink: sponsorUrl || undefined,
    sponsorImg: sponsorImageUrl || undefined,
    funfact: funFact || undefined,
    ...(playerImageBase64 !== null ? { imageBase64: playerImageBase64 } : {}),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitState('submitting');

    try {
      if (isReadingPlayerImage) {
        setErrorMessage('Please wait for the image to finish loading.');
        setSubmitState('idle');
        return;
      }

      if (errorMessage) {
        setSubmitState('idle');
        return;
      }

      const resignationCandidates = archivedPlayers.filter((player) => selectedArchivedPlayers.includes(player.name));

      if (playerMode === 'resign' && resignationCandidates.length === 0) {
        throw new Error('Select at least one archived player to re-sign.');
      }

      if (playerMode === 'fresh' && (!name || !position || !number)) {
        throw new Error('Name, position, and number are required.');
      }

      const payload =
        playerMode === 'resign'
          ? {
              teamName: 'tigers',
              resigning: true,
              players: resignationCandidates.map((player) => normalizeArchivedPlayer(player)),
            }
          : buildFreshPlayerPayload();

      const response = await fetch(addPlayerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(session),
        },
        body: JSON.stringify(payload),
      });

      const data = (await response.json()) as { success?: boolean; message?: string };
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to save player details');
      }

      if (playerMode === 'resign') {
        localStorage.removeItem(CACHE_KEYS.playersArchive);
        localStorage.removeItem(CACHE_KEYS.players);
      }

      setSubmitState('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error while adding player';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  const resetForm = () => {
    setName('');
    setPosition('');
    setNumber('');
    setShoots('');
    setSponsorName('');
    setSponsorUrl('');
    setSponsorImageUrl('');
    setFunFact('');
    setPlayerImageBase64(null);
    setPlayerImageFileName(null);
    setIsReadingPlayerImage(false);
    setImageInputKey((k) => k + 1);
    setSelectedArchivedPlayers([]);
    setPlayerMode('fresh');
    setSubmitState('idle');
    setErrorMessage(null);
  };

  const toggleArchivedPlayer = (playerName: string) => {
    setSelectedArchivedPlayers((current) =>
      current.includes(playerName) ? current.filter((name) => name !== playerName) : [...current, playerName]
    );
  };

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>{playerMode === 'resign' ? 'Player Re-signed' : 'Player Added'}</Title>
        <Message>
          {playerMode === 'resign' ? 'The archived player was added back to the active roster.' : 'The player was added successfully.'}
        </Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            {playerMode === 'resign' ? 'Re-sign more players' : 'Add another player'}
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>{playerMode === 'resign' ? 'Player Not Re-signed' : 'Player Not Added'}</Title>
        <ErrorMessage>
          There was a problem with this player update.
          {errorMessage ? <div style={{ marginTop: '0.5rem' }}>{errorMessage}</div> : null}
        </ErrorMessage>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Add a Player</Title>
      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label>Choose how to add this player</Label>
          <ModePicker>
            <ChoiceLabel>
              <input
                type="radio"
                name="player-mode"
                checked={playerMode === 'fresh'}
                onChange={() => setPlayerMode('fresh')}
              />
              Fresh player
            </ChoiceLabel>
            <ChoiceLabel>
              <input
                type="radio"
                name="player-mode"
                checked={playerMode === 'resign'}
                onChange={() => setPlayerMode('resign')}
              />
              Re-sign from previous year
            </ChoiceLabel>
          </ModePicker>
        </FieldGroup>

        {playerMode === 'resign' ? (
          <FieldGroup>
            <Label>Archived players</Label>
            {isLoadingArchivedPlayers ? <HelperText>Loading archived players...</HelperText> : null}
            {archivedLoadError ? <HelperText>{archivedLoadError}</HelperText> : null}
            {archivedPlayers.length > 0 ? (
              <ArchivedList>
                {archivedPlayers.map((player) => (
                  <ChoiceLabel key={player.name}>
                    <input
                      type="checkbox"
                      checked={selectedArchivedPlayers.includes(player.name)}
                      onChange={() => toggleArchivedPlayer(player.name)}
                    />
                    {player.name}
                  </ChoiceLabel>
                ))}
              </ArchivedList>
            ) : (
              !isLoadingArchivedPlayers && !archivedLoadError ? <HelperText>No archived players available yet.</HelperText> : null
            )}
            <HelperText>
              Select one or more players from the archive to re-sign.
            </HelperText>
          </FieldGroup>
        ) : null}

        {playerMode === 'fresh' ? (
          <>
            <FieldGroup>
              <Label htmlFor="name">
                Name<RequiredMark>*</RequiredMark>
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="position">
                Position<RequiredMark>*</RequiredMark>
              </Label>
              <Select
                id="position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                required
              >
                <option value="">Select position</option>
                <option value="Forward">Forward</option>
                <option value="Defense">Defense</option>
                <option value="Forward/Defense">Forward/Defense</option>
                <option value="Goalie">Goalie</option>
              </Select>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="number">
                Number<RequiredMark>*</RequiredMark>
              </Label>
              <Input
                id="number"
                type="number"
                min="0"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="shoots">Shoots (L/R)</Label>
              <Select
                id="shoots"
                value={shoots}
                onChange={(e) => setShoots(e.target.value)}
              >
                <option value="">Not set</option>
                <option value="Left">L</option>
                <option value="Right">R</option>
              </Select>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="sponsorName">Sponsor Name</Label>
              <Input
                id="sponsorName"
                type="text"
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="sponsorUrl">Sponsor URL</Label>
              <Input
                id="sponsorUrl"
                type="url"
                value={sponsorUrl}
                onChange={(e) => setSponsorUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="sponsorImageUrl">Sponsor Image URL</Label>
              <Input
                id="sponsorImageUrl"
                type="url"
                value={sponsorImageUrl}
                onChange={(e) => setSponsorImageUrl(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="funFact">Fun Fact</Label>
              <Input
                id="funFact"
                type="text"
                value={funFact}
                onChange={(e) => setFunFact(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="playerImage">Player Image (PNG, max 400KB)</Label>
              <FileInput
                key={imageInputKey}
                id="playerImage"
                type="file"
                accept="image/png"
                onChange={(e) => handlePlayerImageFile(e.target.files?.[0] ?? null)}
              />
              {errorMessage ? <UploadError>{errorMessage}</UploadError> : null}
              {playerImageFileName ? (
                <HelperText>
                  Selected: <span style={{ color: '#c9f7d6' }}>{playerImageFileName}</span>
                </HelperText>
              ) : null}
            </FieldGroup>
          </>
        ) : null}

        <SubmitButton
          type="submit"
          disabled={
            submitState === 'submitting' ||
            (playerMode === 'resign' && (isLoadingArchivedPlayers || selectedArchivedPlayers.length === 0)) ||
            (playerMode === 'fresh' && isReadingPlayerImage)
          }
        >
          {isReadingPlayerImage
            ? 'Loading image…'
            : submitState === 'submitting'
              ? 'Submitting…'
              : playerMode === 'resign'
                ? 'Re-sign player(s)'
                : 'Add Player'}
        </SubmitButton>
        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default AddPlayer;
